import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LogIn, Phone, CreditCard, AlertCircle, ArrowLeft,
  Smartphone, ShieldCheck, Zap, FileCheck, KeyRound
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { patientPortalAPI, publicAPI } from '../../services/api';
import LandingNavbar from '../../components/LandingNavbar';

const PatientLogin = () => {
  const navigate = useNavigate();
  const [tenantBranding, setTenantBranding] = useState(null);

  const [step, setStep] = useState(1); // 1 = enter ID+phone, 2 = enter OTP
  const [nationalId, setNationalId] = useState('');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [devOtp, setDevOtp] = useState(null); // Development only — shows OTP

  useEffect(() => {
    const loadBranding = async () => {
      try {
        const hostname = window.location.hostname;
        const parts = hostname.split('.');
        const slug = parts.length > 1 ? parts[0] : null;

        if (slug && slug !== 'www' && slug !== 'app') {
          const { data } = await publicAPI.getBranding(slug);
          if (data.success) setTenantBranding(data.data);
        }
      } catch (error) {
        console.log('No tenant branding found, using defaults');
      }
    };
    loadBranding();
  }, []);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setError('');

    if (!nationalId || !phone) {
      setError('يرجى إدخال رقم الهوية ورقم الجوال');
      return;
    }

    setLoading(true);
    try {
      const res = await patientPortalAPI.sendOTP({ national_id: nationalId, phone });
      setStep(2);
      // Dev mode: show OTP from response
      if (res.data?.data?._dev_otp) setDevOtp(res.data.data._dev_otp);
    } catch (err) {
      setError(err.response?.data?.message || 'فشل إرسال كود التحقق');
    }
    setLoading(false);
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');

    if (!otp) {
      setError('يرجى إدخال كود التحقق');
      return;
    }

    setLoading(true);
    try {
      const res = await patientPortalAPI.verifyOTP({ national_id: nationalId, otp });
      const data = res.data?.data || res.data;
      localStorage.setItem('patientToken', data.token);
      localStorage.setItem('patientData', JSON.stringify(data.customer));
      navigate('/patient/results');
    } catch (err) {
      setError(err.response?.data?.message || 'كود التحقق غير صحيح أو منتهي');
    }
    setLoading(false);
  };

  const handleChangeNumber = () => {
    setStep(1);
    setOtp('');
    setError('');
    setDevOtp(null);
  };

  const features = [
    { icon: Zap, label: 'نتائج فورية', desc: 'اطلع على نتائجك لحظة صدورها' },
    { icon: ShieldCheck, label: 'آمن وسري', desc: 'حماية كاملة لبياناتك' },
    { icon: FileCheck, label: 'سهل الاستخدام', desc: 'واجهة بسيطة وواضحة' },
  ];

  const inputStyle = {
    backgroundColor: '#fff',
    border: '1.5px solid #E5E4E0',
    color: '#222',
  };

  const handleFocus = (e) => {
    e.target.style.borderColor = '#0F6E56';
    e.target.style.boxShadow = '0 0 0 3px rgba(15,110,86,0.08)';
  };

  const handleBlur = (e) => {
    e.target.style.borderColor = '#E5E4E0';
    e.target.style.boxShadow = 'none';
  };

  return (
    <>
      <LandingNavbar />
      {/* Single focused card — the old split-screen decoration (green gradient
          panel with floating shapes + feature cards) competed with the form
          for attention. The client portal is a utility, not a sales page. */}
      <div
        className="min-h-screen flex items-center justify-center px-6 pt-[120px] pb-16 relative"
        dir="rtl"
        style={{ fontFamily: "'Readex Pro', 'Inter', sans-serif", backgroundColor: '#FDFCF9' }}
      >
        {/* Subtle dotted texture for depth without noise. */}
        <div
          className="absolute inset-0 opacity-[0.4] pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, #0F6E56 0.8px, transparent 0)',
            backgroundSize: '28px 28px',
            maskImage: 'radial-gradient(ellipse at center, rgba(0,0,0,0.25), transparent 70%)',
            WebkitMaskImage: 'radial-gradient(ellipse at center, rgba(0,0,0,0.25), transparent 70%)',
          }}
        />

      <div className="w-full relative z-10 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-[440px] bg-white rounded-3xl border border-[#E8E5DD] shadow-[0_10px_40px_-10px_rgba(15,110,86,0.12)] p-8 md:p-10"
        >
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img src="/listech-logo.svg" alt="LIS Techs" className="h-9 w-auto" />
          </div>

          {/* Form header */}
          <div className="mb-7 text-center">
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-medium mb-3"
              style={{ backgroundColor: '#0F6E560F', color: '#0F6E56' }}
            >
              <ShieldCheck className="w-3 h-3" />
              بوابة العملاء
            </span>
            <h2
              className="text-[1.6rem] font-bold mb-1"
              style={{ color: '#111', fontFamily: "'Readex Pro', sans-serif" }}
            >
              تسجيل الدخول
            </h2>
            <p className="text-sm" style={{ color: '#888' }}>
              {step === 1
                ? 'أدخل رقم الهوية ورقم الجوال لإرسال كود التحقق'
                : 'أدخل كود التحقق المرسل إلى جوالك'}
            </p>
          </div>

          {/* Dev OTP banner */}
          {devOtp && step === 2 && (
            <div
              className="mb-5 p-3.5 rounded-xl flex items-center gap-3"
              style={{ backgroundColor: '#FFFBEB', border: '1px solid #FDE68A' }}
            >
              <KeyRound className="w-5 h-5 flex-shrink-0" style={{ color: '#D97706' }} />
              <div>
                <span className="text-xs font-medium" style={{ color: '#92400E' }}>DEV MODE - كود التحقق:</span>
                <span className="font-mono font-bold text-lg mr-2" style={{ color: '#92400E' }} dir="ltr">{devOtp}</span>
              </div>
            </div>
          )}

          {/* Error message */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10, height: 0 }}
                animate={{ opacity: 1, y: 0, height: 'auto' }}
                exit={{ opacity: 0, y: -10, height: 0 }}
                className="mb-5 overflow-hidden"
              >
                <div
                  className="p-3.5 rounded-xl flex items-center gap-3"
                  style={{ backgroundColor: '#FEF2F2', border: '1px solid #FECACA' }}
                >
                  <AlertCircle className="w-5 h-5 flex-shrink-0" style={{ color: '#DC2626' }} />
                  <span className="text-sm" style={{ color: '#B91C1C' }}>{error}</span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Form area */}
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.form
                key="step1"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } }}
                exit={{ opacity: 0, y: -12, transition: { duration: 0.15 } }}
                onSubmit={handleSendOTP}
                className="space-y-5"
              >
                {/* National ID */}
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#444' }}>
                    رقم الهوية
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={nationalId}
                      onChange={(e) => setNationalId(e.target.value)}
                      className="w-full px-4 py-3 pr-11 rounded-xl text-sm transition-all duration-200 outline-none"
                      style={inputStyle}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                      placeholder="أدخل رقم الهوية"
                      dir="ltr"
                    />
                    <CreditCard
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 w-[18px] h-[18px]"
                      style={{ color: '#AAA' }}
                    />
                  </div>
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#444' }}>
                    رقم الجوال
                  </label>
                  <div className="relative">
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="w-full px-4 py-3 pr-11 rounded-xl text-sm transition-all duration-200 outline-none"
                      style={inputStyle}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                      placeholder="05xxxxxxxx"
                      dir="ltr"
                    />
                    <Phone
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 w-[18px] h-[18px]"
                      style={{ color: '#AAA' }}
                    />
                  </div>
                </div>

                {/* Submit */}
                <motion.button
                  type="submit"
                  disabled={loading}
                  whileHover={{ scale: loading ? 1 : 1.01 }}
                  whileTap={{ scale: loading ? 1 : 0.985 }}
                  className="w-full py-3.5 rounded-xl font-semibold text-white text-sm flex items-center justify-center gap-2 transition-all duration-200"
                  style={{
                    background: loading
                      ? '#5BBFA8'
                      : 'linear-gradient(135deg, #0F6E56 0%, #1D9E75 100%)',
                    boxShadow: loading ? 'none' : '0 4px 14px rgba(15,110,86,0.3)',
                    cursor: loading ? 'not-allowed' : 'pointer',
                  }}
                >
                  {loading ? (
                    <>
                      <div
                        className="w-5 h-5 border-2 rounded-full animate-spin"
                        style={{ borderColor: 'rgba(255,255,255,0.3)', borderTopColor: '#fff' }}
                      />
                      <span>جاري الإرسال...</span>
                    </>
                  ) : (
                    <>
                      <Smartphone className="w-5 h-5" />
                      <span>إرسال كود التحقق</span>
                    </>
                  )}
                </motion.button>
              </motion.form>
            )}

            {step === 2 && (
              <motion.form
                key="step2"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } }}
                exit={{ opacity: 0, y: -12, transition: { duration: 0.15 } }}
                onSubmit={handleVerifyOTP}
                className="space-y-5"
              >
                {/* OTP */}
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#444' }}>
                    كود التحقق
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="w-full px-4 py-3 pr-11 rounded-xl text-sm transition-all duration-200 outline-none text-center tracking-[0.5em] font-mono text-lg"
                      style={inputStyle}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                      placeholder="------"
                      dir="ltr"
                      maxLength={6}
                      autoFocus
                    />
                    <KeyRound
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 w-[18px] h-[18px]"
                      style={{ color: '#AAA' }}
                    />
                  </div>
                </div>

                {/* Submit */}
                <motion.button
                  type="submit"
                  disabled={loading}
                  whileHover={{ scale: loading ? 1 : 1.01 }}
                  whileTap={{ scale: loading ? 1 : 0.985 }}
                  className="w-full py-3.5 rounded-xl font-semibold text-white text-sm flex items-center justify-center gap-2 transition-all duration-200"
                  style={{
                    background: loading
                      ? '#5BBFA8'
                      : 'linear-gradient(135deg, #0F6E56 0%, #1D9E75 100%)',
                    boxShadow: loading ? 'none' : '0 4px 14px rgba(15,110,86,0.3)',
                    cursor: loading ? 'not-allowed' : 'pointer',
                  }}
                >
                  {loading ? (
                    <>
                      <div
                        className="w-5 h-5 border-2 rounded-full animate-spin"
                        style={{ borderColor: 'rgba(255,255,255,0.3)', borderTopColor: '#fff' }}
                      />
                      <span>جاري التحقق...</span>
                    </>
                  ) : (
                    <>
                      <LogIn className="w-5 h-5" />
                      <span>تسجيل الدخول</span>
                    </>
                  )}
                </motion.button>

                {/* Change number link */}
                <button
                  type="button"
                  onClick={handleChangeNumber}
                  className="w-full text-center text-sm font-medium transition-colors duration-150 py-2"
                  style={{ color: '#0F6E56' }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#0A5A45'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#0F6E56'}
                >
                  تغيير الرقم
                </button>
              </motion.form>
            )}
          </AnimatePresence>

          {/* Help box */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mt-7 p-4 rounded-xl"
            style={{ backgroundColor: '#F0FAF6', border: '1px solid #D1FAE5' }}
          >
            <p className="text-sm font-semibold mb-1" style={{ color: '#065F46' }}>
              كيف أدخل لنتائجي؟
            </p>
            <p className="text-sm leading-relaxed" style={{ color: '#047857' }}>
              أدخل رقم الهوية ورقم الجوال المسجل لديك وسيصلك كود تحقق. يمكنك أيضاً مسح رمز QR الموجود في الفاتورة للدخول المباشر.
            </p>
          </motion.div>

          {/* Other login links */}
          <div className="mt-6 space-y-2.5 text-center">
            <button
              onClick={() => navigate('/staff-login')}
              className="text-sm flex items-center justify-center gap-2 mx-auto transition-colors duration-150"
              style={{ color: '#888' }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#444'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#888'}
            >
              <ArrowLeft className="w-4 h-4" />
              هل أنت موظف في المختبر؟ تسجيل دخول الموظفين
            </button>
            <button
              onClick={() => navigate('/contractor/login')}
              className="text-sm flex items-center justify-center gap-2 mx-auto transition-colors duration-150"
              style={{ color: '#0F6E56' }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#0A5A45'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#0F6E56'}
            >
              <ArrowLeft className="w-4 h-4" />
              هل أنت مختبر متعاقد؟ بوابة المختبرات المتعاقدة
            </button>
          </div>

          {/* Footer */}
          <p className="mt-8 text-center text-xs" style={{ color: '#BBB' }}>
            &copy; {new Date().getFullYear()} {tenantBranding?.name || 'LIS Techs'}. جميع الحقوق محفوظة
          </p>
        </motion.div>
      </div>
    </div>
    </>
  );
};

export default PatientLogin;
